import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Download, Calendar, Grid, List } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import { formatDate } from '../../utils/helpers';
import { REPORT_TYPE_LABELS } from '../../utils/constants';
import { downloadSimpleReportPDF } from '../../utils/pdfGeneratorSimple';
import { parseReportPayload } from '../../utils/reportPayload';

const ClientReports = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card' - default to table

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const data = await apiService.getReports(user?.id);
      setReports(data);
    } catch (error) {
      console.error('Failed to load reports:', error);
      toast.error('Échec du chargement des rapports');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async (reportType, title) => {
    if (!user?.id) return;

    try {
      setGenerating((prev) => ({ ...prev, [reportType]: true }));
      
      const result = await apiService.generateReport(user.id, reportType);
      
      if (result.success || result.data) {
        toast.success('Rapport généré avec succès ! Téléchargement en cours...');
        
        // Reload reports to get the latest one
        await loadReports();
        
        // Get the latest reports and download the newest one
        const updatedReports = await apiService.getReports(user?.id);
        if (updatedReports && updatedReports.length > 0) {
          // Find the most recent report of this type
          const latestReport = updatedReports
            .filter(r => r.type === reportType)
            .sort((a, b) => new Date(b.generatedAt) - new Date(a.generatedAt))[0];
          
          if (latestReport) {
            // Automatically download it
            handleDownloadReport(latestReport);
          }
        }
      } else {
        toast.error('Erreur lors de la génération du rapport');
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Erreur lors de la génération du rapport');
    } finally {
      setGenerating((prev) => ({ ...prev, [reportType]: false }));
    }
  };

  const handleDownloadReport = (report) => {
    try {
      const data = parseReportPayload(report.fileUrl);
      downloadSimpleReportPDF(data, report.type, {
        name: report.clientName || user?.name,
        company: report.clientCompany || user?.company,
      });
      toast.success('Téléchargement du rapport...');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Mes Rapports</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">
          Téléchargez vos rapports d'équipements et de maintenance
        </p>
      </div>

      {/* Available Reports */}
      <Card>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Rapports disponibles</h2>
        <div className="grid grid-cols-2 gap-2 sm:gap-4">
          <motion.div
            whileHover={{ scale: 1.02, y: -4 }}
            className="p-4 sm:p-6 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all"
          >
            <FileText className="w-8 h-8 sm:w-10 sm:h-10 text-primary-600 mb-2 sm:mb-3" />
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1 sm:mb-2">Rapport Complet</h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
              Liste complète de tous vos équipements avec leur statut actuel
            </p>
            <Button 
              variant="primary" 
              size="sm" 
              icon={Download}
              onClick={() => handleGenerateReport('full', 'Rapport Complet')}
              loading={generating.full}
              className="w-full sm:w-auto"
            >
              Télécharger
            </Button>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -4 }}
            className="p-4 sm:p-6 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all"
          >
            <FileText className="w-8 h-8 sm:w-10 sm:h-10 text-primary-600 mb-2 sm:mb-3" />
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1 sm:mb-2">Maintenances Prévues</h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
              Calendrier des maintenances prévues pour vos équipements
            </p>
            <Button 
              variant="primary" 
              size="sm" 
              icon={Download}
              onClick={() => handleGenerateReport('expiring', 'Maintenances Prévues')}
              loading={generating.expiring}
              className="w-full sm:w-auto"
            >
              Télécharger
            </Button>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -4 }}
            className="p-4 sm:p-6 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all"
          >
            <FileText className="w-8 h-8 sm:w-10 sm:h-10 text-primary-600 mb-2 sm:mb-3" />
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1 sm:mb-2">Historique</h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
              Historique complet de toutes les interventions effectuées
            </p>
            <Button 
              variant="primary" 
              size="sm" 
              icon={Download}
              onClick={() => handleGenerateReport('maintenance', 'Historique')}
              loading={generating.maintenance}
              className="w-full sm:w-auto"
            >
              Télécharger
            </Button>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -4 }}
            className="p-4 sm:p-6 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all"
          >
            <FileText className="w-8 h-8 sm:w-10 sm:h-10 text-primary-600 mb-2 sm:mb-3" />
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1 sm:mb-2">Plan de Masse</h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
              Votre plan de masse avec l'emplacement des équipements
            </p>
            <Button 
              variant="primary" 
              size="sm" 
              icon={Download}
              onClick={() => handleGenerateReport('equipment', 'Plan de Masse')}
              loading={generating.equipment}
              className="w-full sm:w-auto"
            >
              Télécharger
            </Button>
          </motion.div>
        </div>
      </Card>

      {/* Reports History */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Historique des rapports</h2>
          {reports.length > 0 && (
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'table' ? 'primary' : 'outline'}
                size="sm"
                icon={List}
                onClick={() => setViewMode('table')}
              >
                Tableau
              </Button>
              <Button
                variant={viewMode === 'card' ? 'primary' : 'outline'}
                size="sm"
                icon={Grid}
                onClick={() => setViewMode('card')}
              >
                Cartes
              </Button>
            </div>
          )}
        </div>

        {/* Empty State */}
        {reports.length === 0 && (
          <div className="text-center py-6 sm:py-8">
            <FileText className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-2 sm:mb-3" />
            <p className="text-sm sm:text-base text-gray-500">Aucun rapport généré</p>
            <p className="text-xs sm:text-sm text-gray-400 mt-1">Cliquez sur "Télécharger" ci-dessus pour générer un rapport</p>
          </div>
        )}

        {/* Table View */}
        {viewMode === 'table' && reports.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Date de génération
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reports.map((report, index) => {
                  return (
                    <motion.tr
                      key={report.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary-50 rounded-lg">
                            <FileText className="w-5 h-5 text-primary-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {REPORT_TYPE_LABELS[report.type] || report.type}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          {formatDate(new Date(report.generatedAt), 'dd/MM/yyyy HH:mm')}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          icon={Download}
                          onClick={() => handleDownloadReport(report)}
                        >
                          Télécharger PDF
                        </Button>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View */}
        {viewMode === 'card' && reports.length > 0 && (
          <div className="space-y-3">
            {reports.map((report, index) => {
              return (
                <motion.div
                  key={report.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
                    <div className="p-2 sm:p-3 bg-primary-50 rounded-lg flex-shrink-0">
                      <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm sm:text-base text-gray-900 truncate">
                        {REPORT_TYPE_LABELS[report.type] || report.type}
                      </p>
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{formatDate(new Date(report.generatedAt), 'dd/MM/yyyy HH:mm')}</span>
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    icon={Download}
                    onClick={() => handleDownloadReport(report)}
                    className="w-full sm:w-auto"
                  >
                    PDF
                  </Button>
                </motion.div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ClientReports;

